import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import tmdb from '../services/tmdb';
import UserMenu from '../components/UserMenu';
import AjustesMenu from '../components/AjustesMenu';
import './PerfilPeticiones.css';

function PerfilPeticiones({ perfilId: propPerfilId }) {
  const navigate = useNavigate();
  const perfilId = propPerfilId || localStorage.getItem('perfilActivoId');

  const [state, setState] = useState({
    peticiones: [],
    busqueda: '',
    resultados: [],
    seleccionado: null,
    temporadasSeleccionadas: {},
    capitulosSeleccionados: {},
    temporadasDesplegadas: {},
    observaciones: '',
    cargando: false,
    error: null,
    mostrarModal: false,
    seleccionarTodo: false
  });

  const {
    peticiones,
    busqueda,
    resultados,
    seleccionado,
    temporadasSeleccionadas,
    capitulosSeleccionados,
    temporadasDesplegadas,
    observaciones,
    cargando,
    error,
    mostrarModal,
    seleccionarTodo
  } = state;

  useEffect(() => {
    if (!perfilId) {
      console.error('ID de perfil no válido. Redirigiendo...');
      navigate('/seleccionar-perfil', { 
        state: { 
          error: 'Perfil no seleccionado',
          from: 'peticiones'
        } 
      });
      return;
    }

    const cargarPeticiones = async () => {
      try {
        setState(prev => ({ ...prev, cargando: true }));
        
        const res = await api.get(`/peticiones/perfil/${perfilId}`);
        
        if (!res.data || !Array.isArray(res.data)) {
          throw new Error('Formato de respuesta inválido');
        }

        setState(prev => ({
          ...prev,
          peticiones: res.data,
          error: null,
          cargando: false
        }));
      } catch (err) {
        console.error('Error al cargar peticiones:', err);
        setState(prev => ({
          ...prev,
          error: err.response?.data?.error || 'Error al cargar tus peticiones',
          cargando: false
        }));
      }
    };

    cargarPeticiones();
  }, [perfilId, navigate]);

  useEffect(() => {
    if (!busqueda.trim()) {
      setState(prev => ({ ...prev, resultados: [] }));
      return;
    }

    const timer = setTimeout(() => {
      const buscarTMDB = async () => {
        try {
          const res = await tmdb.buscarContenido(busqueda);
          setState(prev => ({ ...prev, resultados: res }));
        } catch (err) {
          console.error('Error al buscar en TMDB:', err);
          setState(prev => ({ 
            ...prev, 
            error: 'Error al buscar contenido' 
          }));
        }
      };

      buscarTMDB();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [busqueda]);

  const seleccionarItem = async (item) => {
    try {
      if (item.media_type !== 'tv') {
        setState(prev => ({ 
          ...prev, 
          seleccionado: { ...item, detalles: null },
          mostrarModal: true
        }));
      } else {
        const detalles = await tmdb.obtenerDetallesSerie(item.id);
        setState(prev => ({
          ...prev,
          seleccionado: { ...item, detalles },
          temporadasSeleccionadas: {},
          capitulosSeleccionados: {},
          temporadasDesplegadas: {},
          mostrarModal: true,
          seleccionarTodo: false
        }));
      }
    } catch (err) {
      console.error('Error al obtener detalles:', err);
      setState(prev => ({ ...prev, error: 'Error al cargar detalles' }));
    }
  };

  const toggleTemporada = (nTemp, episodios) => {
    const checked = !temporadasSeleccionadas[nTemp];
    const nuevosCaps = { ...capitulosSeleccionados };
    
    if (checked) {
      for (let i = 1; i <= episodios; i++) {
        nuevosCaps[`${nTemp}-${i}`] = true;
      }
    } else {
      for (let i = 1; i <= episodios; i++) {
        delete nuevosCaps[`${nTemp}-${i}`];
      }
    }

    setState(prev => ({
      ...prev,
      temporadasSeleccionadas: { ...prev.temporadasSeleccionadas, [nTemp]: checked },
      capitulosSeleccionados: nuevosCaps,
      seleccionarTodo: false
    }));
  };

  const toggleCapitulo = (nTemp, nCap) => {
    const key = `${nTemp}-${nCap}`;
    setState(prev => {
      const nuevo = { ...prev.capitulosSeleccionados };
      if (nuevo[key]) delete nuevo[key];
      else nuevo[key] = true;
      
      const tempSeleccionada = Object.keys(nuevo).some(k => k.startsWith(`${nTemp}-`));
      const tempState = { ...prev.temporadasSeleccionadas };
      tempState[nTemp] = tempSeleccionada;
      
      return { 
        ...prev, 
        capitulosSeleccionados: nuevo,
        temporadasSeleccionadas: tempState,
        seleccionarTodo: false
      };
    });
  };

  const toggleSeleccionarTodo = () => {
    const newSelectAll = !seleccionarTodo;
    let newTempSeleccionadas = {};
    let newCapitulosSeleccionados = {};

    if (newSelectAll && seleccionado?.detalles) {
      seleccionado.detalles.seasons.forEach(season => {
        if (season.season_number > 0) {
          newTempSeleccionadas[season.season_number] = true;
          for (let i = 1; i <= season.episode_count; i++) {
            newCapitulosSeleccionados[`${season.season_number}-${i}`] = true;
          }
        }
      });
    }

    setState(prev => ({
      ...prev,
      seleccionarTodo: newSelectAll,
      temporadasSeleccionadas: newTempSeleccionadas,
      capitulosSeleccionados: newCapitulosSeleccionados
    }));
  };

  const enviarPeticion = async () => {
    if (!seleccionado || !perfilId) {
      setState(prev => ({ ...prev, error: 'Faltan datos requeridos' }));
      return;
    }

    try {
      setState(prev => ({ ...prev, cargando: true }));
      
      const peticion = {
        perfil_id: perfilId,
        titulo: seleccionado.title || seleccionado.name,
        tipo: seleccionado.media_type === 'tv' ? 'serie' : 'pelicula',
        year: (seleccionado.release_date || seleccionado.first_air_date || '').slice(0, 4),
        poster_url: seleccionado.poster_path 
          ? `https://image.tmdb.org/t/p/w500${seleccionado.poster_path}`
          : null,
        sinopsis: seleccionado.overview || '',
        observaciones: observaciones || '',
        temporadas: Object.keys(temporadasSeleccionadas)
          .filter(k => temporadasSeleccionadas[k])
          .map(Number),
        episodios: Object.keys(capitulosSeleccionados)
      };

      const res = await api.post('/peticiones', peticion);
      
      setState(prev => ({
        ...prev,
        peticiones: [...prev.peticiones, {
          ...peticion,
          id: res.data.id,
          estado: 'pendiente',
          url: res.data.urlDisponible,
          created_at: new Date().toISOString()
        }],
        seleccionado: null,
        temporadasSeleccionadas: {},
        capitulosSeleccionados: {},
        observaciones: '',
        resultados: [],
        busqueda: '',
        error: null,
        cargando: false,
        mostrarModal: false,
        seleccionarTodo: false
      }));
      
      if (res.data.urlDisponible) {
        alert(`✅ Petición enviada con éxito.\n\n🎉 ¡El contenido ya está disponible en el servidor!\n\n🔗 Puedes verlo aquí:\n${res.data.urlDisponible}`);
      } else {
        alert('✅ Petición enviada con éxito.');
      }
      
    } catch (err) {
      console.error('Error al enviar petición:', err);
      setState(prev => ({
        ...prev,
        error: err.response?.data?.error || 'Error al enviar petición',
        cargando: false
      }));
    }
  };

  const cerrarModal = () => {
    setState(prev => ({
      ...prev,
      mostrarModal: false,
      seleccionado: null,
      temporadasSeleccionadas: {},
      capitulosSeleccionados: {},
      seleccionarTodo: false
    }));
  };

  if (!perfilId) {
    return (
      
      <div className="peticiones-error">
        <h3>Error de configuración</h3>
        <p>No se ha seleccionado un perfil válido</p>
        <div className="error-actions">
          <button 
            onClick={() => navigate('/seleccionar-perfil')}
            className="btn-primary"
          >
            Seleccionar Perfil
          </button>
          <button 
            onClick={() => window.location.reload()}
            className="btn-secondary"
          >
            Recargar Aplicación
          </button>
        </div>
      </div>
    );
  }

  if (cargando) {
    return (
      <div className="peticiones-cargando">
        <div className="spinner"></div>
        <p>Cargando peticiones...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="peticiones-error">
        <h3>Error</h3>
        <p>{error}</p>
        <div className="error-actions">
          <button onClick={() => setState(prev => ({ ...prev, error: null }))}>
            Aceptar
          </button>
          {error.includes('perfil') && (
            <button onClick={() => window.location.reload()}>Recargar</button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Menús fijos */}
      <div style={{ position: 'fixed', top: '10px', right: '10px', zIndex: 2001 }}>
        <AjustesMenu />
      </div>
      <div style={{ position: 'fixed', top: '10px', left: '10px', zIndex: 2000 }}>
        <UserMenu />
      </div>
    <div className="peticiones-container">
      <div className="busqueda-container">
        <input
          type="text"
          placeholder="Buscar película o serie..."
          value={busqueda}
          onChange={(e) => setState(prev => ({ ...prev, busqueda: e.target.value }))}
          className="busqueda-input"
        />
      </div>

      {resultados.length > 0 && (
        <div className="resultados-container">
          {resultados.map(item => (
            <div 
              key={item.id} 
              className="resultado-item"
              onClick={() => seleccionarItem(item)}
            >
              <img
                src={item.poster_path 
                  ? `https://image.tmdb.org/t/p/w200${item.poster_path}`
                  : '/placeholder-poster.png'
                }
                alt={item.title || item.name}
              />
              <div className="resultado-info">
                <h4>{item.title || item.name}</h4>
                <p>{(item.release_date || item.first_air_date || '').slice(0, 4)}</p>
                <p>{item.media_type === 'tv' ? 'Serie' : 'Película'}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {mostrarModal && seleccionado && (
        <div className="modal-overlay">
          <div className="modal-window">
            <div className="modal-header">
              <div className="contenido-header">
                <img
                  src={seleccionado.poster_path 
                    ? `https://image.tmdb.org/t/p/w200${seleccionado.poster_path}`
                    : '/placeholder-poster.png'
                  }
                  alt={seleccionado.title || seleccionado.name}
                  className="contenido-poster"
                />
                <div className="contenido-info">
                  <h3>{seleccionado.title || seleccionado.name}</h3>
                  <p>{seleccionado.overview}</p>
                  <p>Año: {(seleccionado.release_date || seleccionado.first_air_date || '').slice(0, 4)}</p>
                </div>
              </div>
              <button 
                onClick={cerrarModal}
                className="cerrar-modal"
              >
                ×
              </button>
            </div>

            {seleccionado.media_type === 'tv' && seleccionado.detalles && (
              <div className="seleccion-temporadas">
                <div className="seleccionar-todo">
                  <label>
                    <input
                      type="checkbox"
                      checked={seleccionarTodo}
                      onChange={toggleSeleccionarTodo}
                    />
                    Seleccionar Todos
                  </label>
                </div>

                <div className="temporadas-container">
                  {seleccionado.detalles.seasons
                    .filter(s => s.season_number > 0)
                    .map(temp => {
                      const nTemp = temp.season_number;
                      const episodiosTotales = temp.episode_count;
                      const episodiosSeleccionados = Array.from({ length: episodiosTotales }, (_, i) => `${nTemp}-${i + 1}`)
                        .filter(key => capitulosSeleccionados[key]);

                      const todosSeleccionados = episodiosSeleccionados.length === episodiosTotales;
                      const algunosSeleccionados = episodiosSeleccionados.length > 0 && episodiosSeleccionados.length < episodiosTotales;
                      const desplegado = temporadasDesplegadas[nTemp];

                      return (
                        
                        <div key={nTemp} className="temporada-desplegable">
                          <div className="temporada-header-desplegable">
  <div
    className={`tick-box ${todosSeleccionados ? 'completo' : algunosSeleccionados ? 'parcial' : ''}`}
    onClick={(e) => {
      e.stopPropagation(); // evita que se despliegue la temporada
      toggleTemporada(nTemp, episodiosTotales);
    }}
  >
    {todosSeleccionados && <span className="tick-icon">✓</span>}
    {algunosSeleccionados && <span className="tick-icon">-</span>}
    {!todosSeleccionados && !algunosSeleccionados && <span className="tick-icon"></span>}
  </div>

  <span
    className="temporada-label"
    onClick={() =>
      setState(prev => ({
        ...prev,
        temporadasDesplegadas: {
          ...prev.temporadasDesplegadas,
          [nTemp]: !prev.temporadasDesplegadas[nTemp]
        }
      }))
    }
  >
    Temporada {nTemp} ({episodiosTotales} episodios)
  </span>

  <span
    className="flecha-desplegable"
    onClick={() =>
      setState(prev => ({
        ...prev,
        temporadasDesplegadas: {
          ...prev.temporadasDesplegadas,
          [nTemp]: !prev.temporadasDesplegadas[nTemp]
        }
      }))
    }
  >
    {desplegado ? '▲' : '▼'}
  </span>
</div>

                            

                          {desplegado && (
                            <div className="lista-episodios-desplegable">
                              {Array.from({ length: episodiosTotales }, (_, i) => i + 1).map(ep => (
                                <label key={ep} className="episodio-item">
                                  <input
                                    type="checkbox"
                                    checked={!!capitulosSeleccionados[`${nTemp}-${ep}`]}
                                    onChange={() => toggleCapitulo(nTemp, ep)}
                                  />
                                  Episodio {ep} - {Math.floor(Math.random() * 50) + 30} min
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            <div className="modal-footer">
              <div className="observaciones-container">
                <textarea
                  placeholder="Observaciones adicionales..."
                  value={observaciones}
                  onChange={(e) => setState(prev => ({ ...prev, observaciones: e.target.value }))}
                />
              </div>

              <div className="acciones-modal">
                <button 
                  onClick={cerrarModal}
                  className="btn-cancelar"
                >
                  Cancelar
                </button>
                <button 
                  onClick={enviarPeticion}
                  disabled={cargando}
                  className="enviar-btn"
                >
                  {cargando ? 'Enviando...' : 'Enviar Petición'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="lista-peticiones">
        <h3>Tus peticiones</h3>
        {peticiones.length > 0 ? (
          <ul>
            {peticiones.map((p, index) => (
              <li key={index} className={`peticion-item ${p.estado}`}>
                <div className="peticion-header">
                  <span className="peticion-tipo">{p.tipo === 'serie' ? '📺' : '🎬'}</span>
                  <h4>{p.titulo} ({p.year})</h4>
                  <span className={`estado-badge ${p.estado}`}>
                    {p.estado || 'pendiente'}
                  </span>
                </div>
                
                {(p.temporadas?.length > 0 || p.episodios?.length > 0) && (
                  <div className="peticion-detalles">
                    {p.temporadas?.length > 0 && (
                      <p><strong>Temporadas:</strong> {p.temporadas.join(', ')}</p>
                    )}
                    {p.episodios?.length > 0 && (
                      <p><strong>Capítulos:</strong> {p.episodios.map(e => e.split('-')[1]).join(', ')}</p>
                    )}
                  </div>
                )}
                
                {p.observaciones && (
                  <div className="peticion-observaciones">
                    <p><strong>Observaciones:</strong> {p.observaciones}</p>
                  </div>
                )}
                
                {p.url && (
                  <a href={p.url} target="_blank" rel="noopener noreferrer" className="peticion-enlace">
                    Ver contenido disponible
                  </a>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div className="sin-peticiones">
            <p>No has realizado ninguna petición aún</p>
          </div>
        )}
      </div>
    </div>
    </>
  );
}

export default PerfilPeticiones;